import { Observable, catchError, from, map, of, throwError } from 'rxjs';
import { UAParser } from 'ua-parser-js';
import { LocationTimeoutFailure } from './browser-service.failures';
import { BrowserPermission, BrowserService } from './browser-service.interface';

const ALLOW_FALLBACK_LOCATION_KEY = 'location-force-shared';
const MAX_TIME_TO_WAIT_FOR_LOCATION = 15000;

/**
 * Implementation of the BrowserService interface.
 */
export class BrowserServiceImpl implements BrowserService {
  /**
   * Returns the current permission state of the specified browser permission.
   *
   * @param options An object containing the name of the permission to check.
   * @returns A Promise that resolves with the current state of the permission.
   * @throws An error if the browser does not support the Permissions API.
   */
  getBrowserPermission = (options: {
    name: PermissionName;
  }): Promise<BrowserPermission> =>
    new Promise((resolve, reject) => {
      if (
        window?.navigator?.permissions?.query &&
        typeof window.navigator.permissions.query === 'function'
      ) {
        window.navigator.permissions
          .query({ name: options.name })
          .then((permission) => resolve(permission.state as BrowserPermission));
      } else {
        reject(new Error('Incompatible browser'));
      }
    });

  /**
   * Returns the current geolocation position of the browser.
   *
   * @returns A Promise that resolves with the GeolocationPosition object.
   */
  getBrowserLocation = (): Promise<GeolocationPosition> =>
    new Promise((resolve, reject) => {
      // in case of timeout reject the promise
      const timerId = setTimeout(() => {
        reject(new LocationTimeoutFailure());
      }, MAX_TIME_TO_WAIT_FOR_LOCATION);

      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timerId);
          resolve(position);
        },
        (error) => {
          clearTimeout(timerId);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 300000,
          timeout: 10000,
        },
      );
    });

  userAllowedFallbackConsent(): boolean {
    try {
      const value = localStorage.getItem(ALLOW_FALLBACK_LOCATION_KEY);
      return Boolean(value);
    } catch (err) {
      console.error(
        '[ SharedDataAccess/BrowserService ] Error getting userAllowedFallbackConsent',
        err,
      );
    }

    return false;
  }

  setUserAllowedFallbackConsent(permission: boolean): void {
    try {
      localStorage.setItem(
        ALLOW_FALLBACK_LOCATION_KEY,
        permission ? 'true' : 'false',
      );
    } catch (err) {
      console.error(
        '[ SharedDataAccess/BrowserService ] Error setting setUserAllowedFallbackConsent',
        err,
      );
    }
  }

  getLocationPermission = (): Observable<BrowserPermission> =>
    from(this.getBrowserPermission({ name: 'geolocation' })).pipe(
      map((permission) => {
        return permission;
      }),
      catchError((err) => {
        console.error(
          '[ SharedDataAccess/BrowserService ] Error setting getLocationPermission',
          err,
        );
        return of(BrowserPermission.unknown);
      }),
    );

  getLocation = (): Observable<GeolocationPosition> =>
    from(this.getBrowserLocation()).pipe(
      map((position) => {
        return position;
      }),
      catchError((err) => {
        console.error(
          '[ SharedDataAccess/BrowserService ] Error setting getLocation',
          err,
        );
        return throwError(() => err);
      }),
    );

  isInApp(): boolean {
    const ua = new UAParser();
    const browser = ua.getBrowser();
    const inAppBrowsers = [
      'tiktok',
      'instagram',
      'facebook',
      'slack',
      'twitter',
      'snapchat',
    ];

    return inAppBrowsers.reduce((acc, el) => {
      return acc || browser.name?.toLowerCase() === el.toLowerCase();
    }, false);
  }
}
